import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { smasheursService, tournoisService } from '../services/api';

const Smasheurs = () => {
  const [smasheurs, setSmasheurs] = useState([]);
  const [tournois, setTournois] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'points', direction: 'desc' });

  useEffect(() => {
    loadSmasheurs();
  }, []);

  const loadSmasheurs = async () => {
    try {
      const [smasheursRes, tournoisRes] = await Promise.all([
        smasheursService.getAll(),
        tournoisService.getAll(),
      ]);
      setSmasheurs(smasheursRes.data);
      setTournois(tournoisRes.data);
    } catch (error) {
      console.error('Error loading smasheurs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTournoisCount = (smasheur) => {
    return tournois.filter(t => t.smasheurId === smasheur.idPlayerAPI).length;
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <i className="bi bi-arrow-down-up ms-1"></i>;
    }
    if (sortConfig.direction === 'asc') {
      return <i className="bi bi-arrow-up ms-1"></i>;
    }
    return <i className="bi bi-arrow-down ms-1"></i>;
  };


  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  let filteredSmasheurs = smasheurs.filter((smasheur) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      smasheur.pseudo.toLowerCase().includes(searchLower) ||
      (smasheur.tag && smasheur.tag.toLowerCase().includes(searchLower)) ||
      (smasheur.personnages && smasheur.personnages.toLowerCase().includes(searchLower))
    );
  });

  // Apply sorting
  if (sortConfig.key) {
    filteredSmasheurs = [...filteredSmasheurs].sort((a, b) => {
      let aValue, bValue;
      let aIsEmpty = false;
      let bIsEmpty = false;

      if (sortConfig.key === 'victoires') {
        aValue = getTournoisCount(a);
        bValue = getTournoisCount(b);
      } else if (sortConfig.key === 'points') {
        aValue = a.points || 0;
        bValue = b.points || 0;
      } else if (sortConfig.key === 'pseudo') {
        aValue = a.pseudo.toLowerCase();
        bValue = b.pseudo.toLowerCase();
      } else if (sortConfig.key === 'tag') {
        aValue = (a.tag || '').toLowerCase();
        bValue = (b.tag || '').toLowerCase();
        aIsEmpty = !a.tag || a.tag.trim() === '';
        bIsEmpty = !b.tag || b.tag.trim() === '';
      } else if (sortConfig.key === 'personnages') {
        aValue = (a.personnages || '').toLowerCase();
        bValue = (b.personnages || '').toLowerCase();
        aIsEmpty = !a.personnages || a.personnages.trim() === '';
        bIsEmpty = !b.personnages || b.personnages.trim() === '';
      }

      // Toujours mettre les valeurs vides en bas
      if (aIsEmpty && !bIsEmpty) return 1;
      if (!aIsEmpty && bIsEmpty) return -1;
      if (aIsEmpty && bIsEmpty) return 0;

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  return (
    <div className="container">
      <div className="mb-4">
        <h1>
          <i className="bi bi-people me-2"></i>
          Smasheurs
        </h1>
      </div>

      {/* Barre de recherche */}
      <div className="mb-4">
        <div className="input-group input-group-lg">
          <span className="input-group-text">
            <i className="bi bi-search"></i>
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Rechercher un smasheur par pseudo, tag ou personnage..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              className="btn btn-outline-secondary"
              onClick={() => setSearchTerm('')}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          )}
        </div>
        <small className="text-muted">
          {filteredSmasheurs.length} smasheur{filteredSmasheurs.length > 1 ? 's' : ''} trouvé{filteredSmasheurs.length > 1 ? 's' : ''}
        </small>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th
                    onClick={() => requestSort('pseudo')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Pseudo {getSortIcon('pseudo')}
                  </th>
                  <th
                    onClick={() => requestSort('tag')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Tag {getSortIcon('tag')}
                  </th>
                  <th
                    onClick={() => requestSort('personnages')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Personnages {getSortIcon('personnages')}
                  </th>
                  <th
                    onClick={() => requestSort('points')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Points {getSortIcon('points')}
                  </th>
                  <th
                    onClick={() => requestSort('victoires')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Victoires {getSortIcon('victoires')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredSmasheurs.map((smasheur) => (
                  <tr key={smasheur.id}>
                    <td>
                      <Link
                        to={`/smasheurs/${smasheur.id}`}
                        className="text-decoration-none fw-bold d-flex align-items-center"
                      >
                        {smasheur.profileImage ? (
                          <img
                            src={smasheur.profileImage}
                            alt={smasheur.pseudo}
                            className="rounded-circle me-2"
                            style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                          />
                        ) : (
                          <i className="bi bi-person-circle me-2"></i>
                        )}
                        {smasheur.pseudo}
                      </Link>
                    </td>
                    <td>
                      {smasheur.tag && <span className="badge bg-secondary">{smasheur.tag}</span>}
                    </td>
                    <td className="text-muted small">{smasheur.personnages || '-'}</td>
                    <td>
                      <span className="badge bg-warning text-dark">
                        {smasheur.points || 0}
                      </span>
                    </td>
                    <td>
                      <span className="badge bg-primary">
                        {getTournoisCount(smasheur)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {filteredSmasheurs.length === 0 && (
        <div className="alert alert-info text-center mt-3">
          <i className="bi bi-info-circle me-2"></i>
          Aucun smasheur trouvé pour "{searchTerm}"
        </div>
      )}
    </div>
  );
};

export default Smasheurs;